import React from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, TooltipProvider } from '../../atoms/Tooltip'
import basemapMap from './assets/basemap-map.png'
import basemapSatellite from './assets/basemap-satellite.png'
import { MapCompassIcon, MapLocateIcon, MapZoomInIcon, MapZoomOutIcon } from './MapCornerIcons'
import {
  BasemapLabel,
  BasemapToggle,
  CompassSlot,
  CornerCluster,
  ToolButton,
  ToolColumn,
  ToolGroup,
} from './MapCornerControls.styles'

interface MapCornerControlsProps {
  showNavigation: boolean
  showLocate: boolean
  showBasemap: boolean
  satelliteActive: boolean
  flyToProperty?: boolean
  bearing: number
  onZoomIn: () => void
  onZoomOut: () => void
  onResetNorth: () => void
  onLocate: () => void
  onToggleBasemap: () => void
}

export const MapCornerControls: React.FC<MapCornerControlsProps> = ({
  showNavigation,
  showLocate,
  showBasemap,
  satelliteActive,
  flyToProperty = false,
  bearing,
  onZoomIn,
  onZoomOut,
  onResetNorth,
  onLocate,
  onToggleBasemap,
}) => {
  const { t } = useTranslation('ui')

  if (!showNavigation && !showLocate && !showBasemap) return null

  const basemapLabel = satelliteActive ? t('map.layers.map') : t('map.layers.satellite')
  const basemapTip = satelliteActive ? t('map.controls.showMap') : t('map.controls.showSatellite')
  const locateTip = flyToProperty
    ? t('map.controls.flyToProperty')
    : t('map.controls.flyToLocation')
  const zoomInTip = t('map.controls.zoomIn')
  const zoomOutTip = t('map.controls.zoomOut')
  const resetNorthTip = t('map.controls.resetNorth')

  return (
    <TooltipProvider>
      <CornerCluster>
        {showBasemap && (
          <Tooltip content={basemapTip} placement="top">
            <BasemapToggle type="button" aria-label={basemapTip} onClick={onToggleBasemap}>
              <img src={satelliteActive ? basemapMap : basemapSatellite} alt="" />
              <BasemapLabel $onImagery={!satelliteActive}>{basemapLabel}</BasemapLabel>
            </BasemapToggle>
          </Tooltip>
        )}
        {(showNavigation || showLocate) && (
          <ToolColumn>
            {showNavigation && (
              <ToolGroup>
                <Tooltip content={zoomInTip} placement="top">
                  <ToolButton type="button" aria-label={zoomInTip} onClick={onZoomIn}>
                    <MapZoomInIcon />
                  </ToolButton>
                </Tooltip>
                <Tooltip content={zoomOutTip} placement="top">
                  <ToolButton type="button" aria-label={zoomOutTip} onClick={onZoomOut}>
                    <MapZoomOutIcon />
                  </ToolButton>
                </Tooltip>
                <Tooltip content={resetNorthTip} placement="top">
                  <ToolButton type="button" aria-label={resetNorthTip} onClick={onResetNorth}>
                    <CompassSlot $bearing={bearing}>
                      <MapCompassIcon />
                    </CompassSlot>
                  </ToolButton>
                </Tooltip>
              </ToolGroup>
            )}
            {showLocate && (
              <ToolGroup>
                <Tooltip content={locateTip} placement="top">
                  <ToolButton type="button" aria-label={locateTip} onClick={onLocate}>
                    <MapLocateIcon />
                  </ToolButton>
                </Tooltip>
              </ToolGroup>
            )}
          </ToolColumn>
        )}
      </CornerCluster>
    </TooltipProvider>
  )
}
